/**
 * Text Normalization Utility
 * 
 * Handles Unicode normalization for proper display of Indic scripts like Kannada
 * Ensures consistent rendering across different browsers and platforms
 */

/**
 * Normalize text to NFC (Canonical Composition) form
 * This ensures Kannada and other Indic script characters are displayed consistently
 * 
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export function normalizeText(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }
  
  // Normalize to NFC (Canonical Composition)
  // This combines decomposed characters into their composed form
  return text.normalize('NFC');
}

/**
 * Normalize text to NFD (Canonical Decomposition) form
 * Useful for comparison and searching
 * 
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export function normalizeTextForComparison(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }
  
  // Normalize to NFD (Canonical Decomposition)
  return text.normalize('NFD');
}

/**
 * Compare two texts after normalization
 * 
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {boolean} - Whether texts are equal after normalization
 */
export function compareNormalizedText(text1, text2) {
  return normalizeTextForComparison(text1) === normalizeTextForComparison(text2);
}

/**
 * Normalize an object's text properties
 * Useful for normalizing data from database
 * 
 * @param {Object} obj - Object with text properties
 * @param {Array<string>} properties - Array of property names to normalize
 * @returns {Object} - Object with normalized text properties
 */
export function normalizeObjectText(obj, properties) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const normalized = { ...obj };
  
  properties.forEach(prop => {
    if (normalized[prop] && typeof normalized[prop] === 'string') {
      normalized[prop] = normalizeText(normalized[prop]);
    }
  });
  
  return normalized;
}
